// SPDX-License-Identifier: AGPL-3.0-or-later
//
// parley: the soundboard panel above the voice bar (strand AJ,
// docs/strands/soundboard.md). Strand AG opened a plain action menu here;
// this is the picker the product asked for: a search field, jump marks on
// the left, folding sections and a grid of tiles.
//
// It is built from the pieces the expression picker already uses
// (PickerSearchInput, Scroller, GuildIcon, Phosphor icons, the theme's CSS
// variables), so it looks like the rest of the client and not like a guest
// from another application.
//
// Why only this community's sounds: playing one calls
// POST /voice/channels/:id/soundboard-sound, and the API only accepts a
// sound of that channel's own guild (there is no USE_EXTERNAL_SOUNDS here).
// Listing other communities would offer tiles that cannot work, so the
// panel says so in one line instead.

import {SoundboardEmoji} from '@app/parley/soundboard/SoundboardEmoji';
import {
	SOUNDBOARD_ADD_FAVORITE_DESCRIPTOR,
	SOUNDBOARD_COLLAPSE_SECTION_DESCRIPTOR,
	SOUNDBOARD_DESCRIPTOR,
	SOUNDBOARD_EMPTY_DESCRIPTOR,
	SOUNDBOARD_EMPTY_HINT_DESCRIPTOR,
	SOUNDBOARD_EXPAND_SECTION_DESCRIPTOR,
	SOUNDBOARD_FAVORITES_DESCRIPTOR,
	SOUNDBOARD_GUILD_SECTION_DESCRIPTOR,
	SOUNDBOARD_NO_RESULTS_DESCRIPTOR,
	SOUNDBOARD_ONLY_THIS_COMMUNITY_DESCRIPTOR,
	SOUNDBOARD_PANEL_VOLUME_DESCRIPTOR,
	SOUNDBOARD_PLAY_FAILED_DESCRIPTOR,
	SOUNDBOARD_PREVIEW_DESCRIPTOR,
	SOUNDBOARD_RECENT_DESCRIPTOR,
	SOUNDBOARD_REMOVE_FAVORITE_DESCRIPTOR,
	SOUNDBOARD_SEARCH_DESCRIPTOR,
	SOUNDBOARD_STOP_DESCRIPTOR,
} from '@app/parley/soundboard/SoundboardMessages';
import styles from '@app/parley/soundboard/SoundboardPanel.module.css';
import SoundboardPlaybackEngine from '@app/parley/soundboard/SoundboardPlaybackEngine';
import SoundboardPreferences from '@app/parley/soundboard/SoundboardPreferences';
import SoundboardStore, {type SoundboardSound} from '@app/parley/soundboard/SoundboardStore';
import SoundboardVolume, {
	SOUNDBOARD_VOLUME_MAX,
	SOUNDBOARD_VOLUME_MIN,
} from '@app/parley/soundboard/SoundboardVolume';
import {PickerSearchInput} from '@app/features/channel/components/shared/PickerSearchInput';
import {GuildIcon} from '@app/features/guild/components/popouts/GuildIcon';
import Guilds from '@app/features/guild/state/Guilds';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Scroller, type ScrollerHandle} from '@app/features/ui/components/Scroller';
import {Slider} from '@app/features/ui/components/Slider';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {useLingui} from '@lingui/react/macro';
import {
	CaretDownIcon,
	ClockIcon,
	SpeakerHighIcon,
	SpeakerLowIcon,
	SpeakerSimpleHighIcon,
	SpeakerSlashIcon,
	SpeakerXIcon,
	StarIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {matchSorter} from 'match-sorter';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const logger = new Logger('SoundboardPanel');

/** Tiles per row, mirrored in the grid CSS; the arrow keys step by this. */
const COLUMNS = 3;
const FAVORITES_SECTION_ID = 'favorites';
const RECENT_SECTION_ID = 'recent';
const SEARCH_SECTION_ID = 'search';

interface PanelSection {
	id: string;
	/** Empty for the search results: they carry no heading and no mark. */
	title: string;
	mark: React.ReactNode;
	sounds: ReadonlyArray<SoundboardSound>;
}

const SoundTile = observer(
	({
		sound,
		index,
		registerTile,
		onPlay,
		onGridKeyDown,
	}: {
		sound: SoundboardSound;
		index: number;
		registerTile: (index: number, node: HTMLButtonElement | null) => void;
		onPlay: (sound: SoundboardSound) => void;
		onGridKeyDown: (event: React.KeyboardEvent, index: number) => void;
	}) => {
		const {i18n} = useLingui();
		const isFavorite = SoundboardPreferences.isFavorite(sound.soundId);
		const playing = SoundboardPlaybackEngine.playing.get(sound.hash);
		const favoriteLabel = i18n._(
			isFavorite ? SOUNDBOARD_REMOVE_FAVORITE_DESCRIPTOR : SOUNDBOARD_ADD_FAVORITE_DESCRIPTOR,
		);
		const previewLabel = i18n._(SOUNDBOARD_PREVIEW_DESCRIPTOR);
		// Only you hear a preview; the channel hears the tile itself.
		const preview = useCallback(() => {
			void SoundboardPlaybackEngine.play({
				userId: sound.userId,
				hash: sound.hash,
				url: sound.url,
				durationMs: sound.durationMs,
				volume: sound.volume,
			});
		}, [sound]);
		return (
			<div
				className={clsx(
					styles.tileWrapper,
					playing && styles.tileWrapperPlaying,
					isFavorite && styles.tileWrapperFavorite,
				)}
				data-flx="parley.soundboard.soundboard-panel.sound-tile.tile-wrapper"
			>
				<FocusRing offset={-2} data-flx="parley.soundboard.soundboard-panel.sound-tile.focus-ring">
					<button
						type="button"
						ref={(node) => registerTile(index, node)}
						className={styles.tile}
						onClick={() => onPlay(sound)}
						onKeyDown={(event) => onGridKeyDown(event, index)}
						data-flx="parley.soundboard.soundboard-panel.sound-tile.tile.play.button"
					>
						{playing && (
							// Restarts with every play: the key is the start time.
							<span
								key={playing.startedAt}
								className={styles.tileProgress}
								style={{animationDuration: `${Math.max(200, playing.durationMs)}ms`}}
								aria-hidden="true"
								data-flx="parley.soundboard.soundboard-panel.sound-tile.progress"
							/>
						)}
						<SoundboardEmoji
							emojiId={sound.emojiId}
							emojiName={sound.emojiName}
							className={styles.tileEmoji}
							data-flx="parley.soundboard.soundboard-panel.sound-tile.tile-emoji"
						/>
						<span className={styles.tileName} data-flx="parley.soundboard.soundboard-panel.sound-tile.tile-name">
							{sound.name}
						</span>
					</button>
				</FocusRing>
				<div className={styles.tileActions} data-flx="parley.soundboard.soundboard-panel.sound-tile.actions">
					<Tooltip text={previewLabel} data-flx="parley.soundboard.soundboard-panel.sound-tile.tooltip.preview">
						<button
							type="button"
							className={styles.tileAction}
							aria-label={`${previewLabel}: ${sound.name}`}
							onClick={preview}
							data-flx="parley.soundboard.soundboard-panel.sound-tile.button.preview"
						>
							<SpeakerSimpleHighIcon
								weight="fill"
								className={styles.tileActionIcon}
								data-flx="parley.soundboard.soundboard-panel.sound-tile.preview-icon"
							/>
						</button>
					</Tooltip>
					<Tooltip text={favoriteLabel} data-flx="parley.soundboard.soundboard-panel.sound-tile.tooltip">
						<button
							type="button"
							className={clsx(styles.tileAction, isFavorite && styles.tileActionOn)}
							aria-label={favoriteLabel}
							aria-pressed={isFavorite}
							onClick={() => SoundboardPreferences.toggleFavorite(sound.soundId)}
							data-flx="parley.soundboard.soundboard-panel.sound-tile.favorite-button.toggle-favorite"
						>
							<StarIcon
								weight={isFavorite ? 'fill' : 'regular'}
								className={styles.tileActionIcon}
								data-flx="parley.soundboard.soundboard-panel.sound-tile.favorite-icon"
							/>
						</button>
					</Tooltip>
				</div>
			</div>
		);
	},
);

/** Speaker icon that shows roughly how loud the soundboard is. */
const VolumeIcon = observer(({volume, className}: {volume: number; className?: string}): React.ReactElement => {
	if (volume <= 0)
		return (
			<SpeakerXIcon
				weight="fill"
				className={className}
				data-flx="parley.soundboard.soundboard-panel.volume-icon.speaker-x-icon"
			/>
		);
	if (volume < 50)
		return (
			<SpeakerLowIcon
				weight="fill"
				className={className}
				data-flx="parley.soundboard.soundboard-panel.volume-icon.speaker-low-icon"
			/>
		);
	return (
		<SpeakerHighIcon
			weight="fill"
			className={className}
			data-flx="parley.soundboard.soundboard-panel.volume-icon.speaker-high-icon"
		/>
	);
});

export const SoundboardPanel = observer(
	({
		guildId,
		channelId,
		variant = 'popout',
	}: {
		guildId: string;
		channelId: string;
		/** In a bottom sheet the jump marks lie flat above the list. */
		variant?: 'popout' | 'sheet';
	}): React.ReactElement => {
		const {i18n} = useLingui();
		const [query, setQuery] = useState('');
		const [volumeOpen, setVolumeOpen] = useState(false);
		const volumeButtonRef = useRef<HTMLButtonElement>(null);
		const scrollerRef = useRef<ScrollerHandle>(null);
		const searchRef = useRef<HTMLInputElement>(null);
		const sectionRefs = useRef(new Map<string, HTMLDivElement>());
		const tileRefs = useRef<Array<HTMLButtonElement | null>>([]);
		const guild = Guilds.getGuild(guildId);
		const sounds = SoundboardStore.availableForGuild(guildId);
		const trimmedQuery = query.trim();

		useEffect(() => {
			void SoundboardStore.load(guildId);
		}, [guildId]);

		const play = useCallback(
			(sound: SoundboardSound) => {
				// The panel stays open on purpose: pressing two sounds in a row
				// is the normal case, and everyone - the presser included -
				// hears them through the gateway dispatch.
				SoundboardPreferences.trackPlay(sound.soundId);
				SoundboardStore.play(channelId, sound.soundId).catch((error) => {
					logger.debug('Failed to play a soundboard sound', {error});
					ToastCommands.error(i18n._(SOUNDBOARD_PLAY_FAILED_DESCRIPTOR));
				});
			},
			[channelId, i18n],
		);

		const sections = useMemo<Array<PanelSection>>(() => {
			if (trimmedQuery.length > 0) {
				return [
					{
						id: SEARCH_SECTION_ID,
						title: '',
						mark: null,
						sounds: matchSorter(sounds.slice(), trimmedQuery, {keys: ['name']}),
					},
				];
			}
			const byId = new Map(sounds.map((sound) => [sound.soundId, sound]));
			const pick = (ids: ReadonlyArray<string>): Array<SoundboardSound> =>
				ids.map((soundId) => byId.get(soundId)).filter((sound): sound is SoundboardSound => sound != null);
			const favorites = pick(SoundboardPreferences.favoriteSounds);
			const recent = pick(SoundboardPreferences.recentSoundIds());
			const result: Array<PanelSection> = [];
			if (favorites.length > 0) {
				result.push({
					id: FAVORITES_SECTION_ID,
					title: i18n._(SOUNDBOARD_FAVORITES_DESCRIPTOR),
					mark: (
						<StarIcon
							weight="fill"
							className={styles.markIcon}
							data-flx="parley.soundboard.soundboard-panel.sections.mark-icon"
						/>
					),
					sounds: favorites,
				});
			}
			if (recent.length > 0) {
				result.push({
					id: RECENT_SECTION_ID,
					title: i18n._(SOUNDBOARD_RECENT_DESCRIPTOR),
					mark: (
						<ClockIcon
							weight="fill"
							className={styles.markIcon}
							data-flx="parley.soundboard.soundboard-panel.sections.mark-icon--2"
						/>
					),
					sounds: recent,
				});
			}
			if (sounds.length > 0) {
				result.push({
					id: `guild:${guildId}`,
					title: i18n._(SOUNDBOARD_GUILD_SECTION_DESCRIPTOR, {guildName: guild?.name ?? ''}),
					mark: (
						<GuildIcon
							id={guildId}
							name={guild?.name ?? ''}
							icon={guild?.icon ?? null}
							className={styles.markIcon}
							sizePx={20}
							data-flx="parley.soundboard.soundboard-panel.sections.mark-icon--3"
						/>
					),
					sounds,
				});
			}
			return result;
		}, [sounds, trimmedQuery, guildId, guild?.name, guild?.icon, i18n]);

		// One flat order over every visible tile: the arrow keys walk it, so a
		// section boundary is not a dead end. The index of a tile is its
		// position in this list.
		const tileIndexByKey = useMemo(() => {
			const index = new Map<string, number>();
			let next = 0;
			for (const section of sections) {
				if (SoundboardPreferences.isSectionCollapsed(section.id)) continue;
				for (const sound of section.sounds) {
					index.set(`${section.id}:${sound.soundId}`, next++);
				}
			}
			tileRefs.current.length = next;
			return index;
		}, [sections]);

		const firstVisibleSound = useMemo(() => {
			for (const section of sections) {
				if (SoundboardPreferences.isSectionCollapsed(section.id)) continue;
				if (section.sounds.length > 0) return section.sounds[0];
			}
			return null;
		}, [sections]);

		const registerTile = useCallback((index: number, node: HTMLButtonElement | null) => {
			tileRefs.current[index] = node;
		}, []);

		const focusTile = useCallback((index: number) => {
			if (tileRefs.current.length === 0) return;
			const clamped = Math.max(0, Math.min(tileRefs.current.length - 1, index));
			tileRefs.current[clamped]?.focus();
		}, []);

		const handleGridKeyDown = useCallback(
			(event: React.KeyboardEvent, index: number) => {
				switch (event.key) {
					case 'ArrowRight':
						event.preventDefault();
						focusTile(index + 1);
						return;
					case 'ArrowLeft':
						event.preventDefault();
						focusTile(index - 1);
						return;
					case 'ArrowDown':
						event.preventDefault();
						focusTile(index + COLUMNS);
						return;
					case 'ArrowUp':
						event.preventDefault();
						// Above the top row is the search field, not nothing.
						if (index < COLUMNS) searchRef.current?.focus();
						else focusTile(index - COLUMNS);
						return;
					case 'Home':
						event.preventDefault();
						focusTile(0);
						return;
					case 'End':
						event.preventDefault();
						focusTile(tileRefs.current.length - 1);
						return;
					default:
				}
			},
			[focusTile],
		);

		const handleSearchKeyDown = useCallback(
			(event: React.KeyboardEvent<HTMLInputElement>) => {
				if (event.key === 'ArrowDown') {
					event.preventDefault();
					focusTile(0);
					return;
				}
				// Enter plays the best match, the way a search field should.
				if (event.key === 'Enter' && firstVisibleSound) {
					event.preventDefault();
					play(firstVisibleSound);
				}
			},
			[firstVisibleSound, focusTile, play],
		);

		const jumpToSection = useCallback((sectionId: string) => {
			const node = sectionRefs.current.get(sectionId);
			if (node) scrollerRef.current?.revealElement({node, alignment: 'start', animate: true});
		}, []);

		const jumpMarks = sections.filter((section) => section.title !== '');
		return (
			<section
				className={clsx(styles.panel, variant === 'sheet' && styles.panelSheet)}
				aria-label={i18n._(SOUNDBOARD_DESCRIPTOR)}
				data-flx="parley.soundboard.soundboard-panel.panel"
			>
				<div className={styles.header} data-flx="parley.soundboard.soundboard-panel.header">
					<div className={styles.search} data-flx="parley.soundboard.soundboard-panel.search">
						<PickerSearchInput
							value={query}
							onChange={setQuery}
							inputRef={searchRef}
							placeholder={i18n._(SOUNDBOARD_SEARCH_DESCRIPTOR)}
							onKeyDown={handleSearchKeyDown}
							data-flx="parley.soundboard.soundboard-panel.picker-search-input.set-query"
						/>
					</div>
					<Popout
						position="bottom-end"
						animationType="none"
						offsetMainAxis={6}
						offsetCrossAxis={0}
						onOpen={() => setVolumeOpen(true)}
						onClose={() => setVolumeOpen(false)}
						returnFocusRef={volumeButtonRef}
						render={() => (
							<div
								className={styles.volumePopout}
								role="dialog"
								aria-label={i18n._(SOUNDBOARD_PANEL_VOLUME_DESCRIPTOR)}
								data-flx="parley.soundboard.soundboard-panel.volume-popout"
							>
								<div className={styles.volumeHeader} data-flx="parley.soundboard.soundboard-panel.volume-header">
									<span className={styles.volumeLabel} data-flx="parley.soundboard.soundboard-panel.volume-label">
										{i18n._(SOUNDBOARD_PANEL_VOLUME_DESCRIPTOR)}
									</span>
									<span className={styles.volumeValue} data-flx="parley.soundboard.soundboard-panel.volume-value">
										{SoundboardVolume.volume}%
									</span>
								</div>
								<Slider
									className={styles.volumeSlider}
									value={SoundboardVolume.volume}
									defaultValue={SoundboardVolume.volume}
									factoryDefaultValue={SOUNDBOARD_VOLUME_MAX}
									minValue={SOUNDBOARD_VOLUME_MIN}
									maxValue={SOUNDBOARD_VOLUME_MAX}
									step={1}
									ariaLabel={i18n._(SOUNDBOARD_PANEL_VOLUME_DESCRIPTOR)}
									onValueRender={(value) => `${Math.round(value)}%`}
									asValueChanges={(value) => SoundboardVolume.setVolume(value)}
									onValueChange={(value) => SoundboardVolume.setVolume(value)}
									data-flx="parley.soundboard.soundboard-panel.volume-slider"
								/>
							</div>
						)}
						data-flx="parley.soundboard.soundboard-panel.volume.popout"
					>
						<FocusRing offset={-2} data-flx="parley.soundboard.soundboard-panel.focus-ring.volume">
							<button
								ref={volumeButtonRef}
								type="button"
								className={clsx(styles.headerButton, volumeOpen && styles.headerButtonActive)}
								aria-label={i18n._(SOUNDBOARD_PANEL_VOLUME_DESCRIPTOR)}
								aria-haspopup="dialog"
								aria-expanded={volumeOpen}
								data-flx="parley.soundboard.soundboard-panel.volume-button.toggle"
							>
								<VolumeIcon
									volume={SoundboardVolume.volume}
									className={styles.headerIcon}
									data-flx="parley.soundboard.soundboard-panel.volume-icon"
								/>
							</button>
						</FocusRing>
					</Popout>
					<Tooltip text={i18n._(SOUNDBOARD_STOP_DESCRIPTOR)} data-flx="parley.soundboard.soundboard-panel.tooltip">
						<FocusRing offset={-2} data-flx="parley.soundboard.soundboard-panel.focus-ring">
							<button
								type="button"
								className={styles.headerButton}
								aria-label={i18n._(SOUNDBOARD_STOP_DESCRIPTOR)}
								onClick={() => SoundboardPlaybackEngine.stopAll()}
								data-flx="parley.soundboard.soundboard-panel.stop-button.stop-all"
							>
								<SpeakerSlashIcon
									weight="fill"
									className={styles.headerIcon}
									data-flx="parley.soundboard.soundboard-panel.stop-icon"
								/>
							</button>
						</FocusRing>
					</Tooltip>
				</div>
				<div className={styles.body} data-flx="parley.soundboard.soundboard-panel.body">
					{jumpMarks.length > 1 && (
						<div className={styles.rail} data-flx="parley.soundboard.soundboard-panel.rail">
							{jumpMarks.map((section) => (
								<Tooltip
									key={section.id}
									text={section.title}
									position={variant === 'sheet' ? 'top' : 'right'}
									data-flx="parley.soundboard.soundboard-panel.tooltip--2"
								>
									<button
										type="button"
										className={styles.railButton}
										aria-label={section.title}
										onClick={() => jumpToSection(section.id)}
										data-flx="parley.soundboard.soundboard-panel.rail-button.jump-to-section"
									>
										{section.mark}
									</button>
								</Tooltip>
							))}
						</div>
					)}
					<Scroller
						ref={scrollerRef}
						className={styles.list}
						fade={false}
						data-flx="parley.soundboard.soundboard-panel.list"
					>
						{sections.length === 0 && (
							<div className={styles.empty} data-flx="parley.soundboard.soundboard-panel.empty">
								<p className={styles.emptyTitle} data-flx="parley.soundboard.soundboard-panel.empty-title">
									{i18n._(trimmedQuery ? SOUNDBOARD_NO_RESULTS_DESCRIPTOR : SOUNDBOARD_EMPTY_DESCRIPTOR)}
								</p>
								{!trimmedQuery && (
									<p className={styles.emptyHint} data-flx="parley.soundboard.soundboard-panel.empty-hint">
										{i18n._(SOUNDBOARD_EMPTY_HINT_DESCRIPTOR)}
									</p>
								)}
							</div>
						)}
						{sections.map((section) => {
							const collapsed = SoundboardPreferences.isSectionCollapsed(section.id);
							return (
								<div
									key={section.id}
									className={styles.section}
									ref={(node) => {
										if (node) sectionRefs.current.set(section.id, node);
										else sectionRefs.current.delete(section.id);
									}}
									data-flx="parley.soundboard.soundboard-panel.section"
								>
									{section.title !== '' && (
										<button
											type="button"
											className={styles.sectionHeader}
											onClick={() => SoundboardPreferences.toggleSection(section.id)}
											aria-expanded={!collapsed}
											aria-label={`${section.title} - ${i18n._(
												collapsed ? SOUNDBOARD_EXPAND_SECTION_DESCRIPTOR : SOUNDBOARD_COLLAPSE_SECTION_DESCRIPTOR,
											)}`}
											data-flx="parley.soundboard.soundboard-panel.section-header.toggle-section.button"
										>
											{section.mark}
											<span
												className={styles.sectionTitle}
												data-flx="parley.soundboard.soundboard-panel.section-title"
											>
												{section.title}
											</span>
											<CaretDownIcon
												weight="bold"
												className={clsx(styles.sectionCaret, collapsed && styles.sectionCaretCollapsed)}
												data-flx="parley.soundboard.soundboard-panel.section-caret"
											/>
										</button>
									)}
									{!collapsed && (
										<div className={styles.grid} data-flx="parley.soundboard.soundboard-panel.grid">
											{section.sounds.map((sound) => (
												<SoundTile
													key={`${section.id}:${sound.soundId}`}
													sound={sound}
													index={tileIndexByKey.get(`${section.id}:${sound.soundId}`) ?? 0}
													registerTile={registerTile}
													onPlay={play}
													onGridKeyDown={handleGridKeyDown}
													data-flx="parley.soundboard.soundboard-panel.sound-tile"
												/>
											))}
										</div>
									)}
								</div>
							);
						})}
						{sections.length > 0 && (
							<p className={styles.footnote} data-flx="parley.soundboard.soundboard-panel.footnote">
								{i18n._(SOUNDBOARD_ONLY_THIS_COMMUNITY_DESCRIPTOR)}
							</p>
						)}
					</Scroller>
				</div>
			</section>
		);
	},
);

export default SoundboardPanel;
